import { useMemo, useState } from "react";
import useFetchNoPaging from "#src/hooks/useFetchNoPaging";
import paymentService from "#src/services/payment";
import {
  createSearchParams,
  generatePath,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { useAppSelector } from "#src/store/hooks";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";
import { useTranslation } from "react-i18next";
import {
  ArrowUp,
  CheckCircle,
  Clock,
  CreditCard02,
  CreditCardCheck,
  InfoCircle,
  Repeat02,
  Trash03,
} from "@untitled-ui/icons-react";
import { SearchboxV2 } from "#src/commonV2/Table/Searchbox";
import { TableV2 } from "#src/commonV2/Table";
import { expandPlanDuration } from "#src/utils/apiHelper";
import { DateTime } from "luxon";
import { TableActionLabelV2 } from "#src/commonV2/Table/TableActionLabel";
import { pathname } from "#src/config/pathname";
import {
  ActionItem,
  TableActionButtonV2,
} from "#src/commonV2/Table/TableActionButton";
import {
  IWorkspacePaymentPlanItem,
  IRecurringPrice,
} from "#src/services/payment/types";
import {
  PaymentMethodValue,
  SolutionStatusValue,
} from "#src/services/payment/enum";
import { CustomBadgeV2ForStatus } from "#src/components/workspace/billing/CustomBadge";
import { BillingServiceUnsubModal } from "#src/components/workspace/billing/solution/UnsubModal";
import {
  SolutionAction,
  upgradeUrl,
} from "#src/components/workspace/billing/solution/enum";
import {
  solutionStatusItems,
  paymentMethodItems,
} from "#src/components/workspace/billing/solution/filterItems";
import { FilterDropdownV2 } from "#src/commonV2/Table/Filter/FilterDropdown";

export enum BillingSolutionSearchParamKey {
  STATUS = "status",
  PAYMENT_METHOD = "payment_method",
}

export const BillingSolutionTab = () => {
  const { workspaceId } = useOutletContext<{
    workspaceId: string;
  }>();

  const { t, i18n } = useTranslation("workspace", {
    keyPrefix: "billing.tab.solution",
  });

  const currentWorkspace = useAppSelector(
    (state) => state.auth.currentWorkspace
  );

  const locale = i18n.language;

  const navigate = useNavigate();

  // --------------- STATES ---------------

  // Search/sort
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Selection
  // const [selectedSolutionIds, setSelectedSolutionIds] = useState<string[]>([]);

  // Unsub modal
  const [unsubbingService, setUnsubbingService] =
    useState<IWorkspacePaymentPlanItem | null>(null);

  const { value: status, renderProps: statusFilterRenderProps } = useFilter({
    label: t("filter.status.label"),
    selectionList: solutionStatusItems,
    options: {
      searchParamKey: BillingSolutionSearchParamKey.STATUS,
    },
  });

  const { value: paymentMethod, renderProps: paymentMethodFilterRenderProps } =
    useFilter({
      label: t("filter.paymentMethod.label"),
      selectionList: paymentMethodItems,
      options: {
        searchParamKey: BillingSolutionSearchParamKey.PAYMENT_METHOD,
      },
    });

  // --------------- METHODS ---------------

  const actionList: ActionItem<IWorkspacePaymentPlanItem>[] = [
    {
      key: SolutionAction.UPGRADE_PLAN,
      label: (
        <TableActionLabelV2
          icon={<ArrowUp className="h-5 w-5 min-w-5 text-gray-v2" />}
          text={t("actions.upgradePlan")}
        />
      ),
      action: (item) => {
        if (Object.keys(upgradeUrl).includes(item.product_plan.id)) {
          navigate(
            generatePath(upgradeUrl[item.product_plan.id], {
              workspaceId,
            })
          );
        }
      },
    },
    {
      key: SolutionAction.RENEW,
      label: (
        <TableActionLabelV2
          icon={<CreditCardCheck className="h-5 w-5 min-w-5 text-gray-v2" />}
          text={t("actions.renew")}
        />
      ),
      action: (item) => {
        navigate({
          pathname: generatePath(pathname.WORKSPACE_BILLING_RENEW, {
            workspaceId,
          }),
          search: createSearchParams({
            product_id: item.product.id,
          }).toString(),
        });
      },
    },
    {
      key: SolutionAction.UNSUBSCRIBE,
      label: (
        <TableActionLabelV2
          icon={<Trash03 className="h-5 w-5 min-w-5" />}
          text={t("actions.unsubscribe")}
          warning
        />
      ),
      action: (item) => {
        setUnsubbingService(item);
      },
    },
  ];

  const onCloseUnsubModal = () => {
    setUnsubbingService(null);
  };

  // --------------- COMPUTED ---------------

  const fetchParams = useMemo<
    Parameters<typeof paymentService.listWorkspacePaymentPlans>
  >(
    () => [
      workspaceId,
      {
        status,
        payment_method: paymentMethod,
        q: searchKeyword,
      },
    ],
    [workspaceId, status, paymentMethod, searchKeyword]
  );

  // --------------- HOOKS ---------------

  const { list: solutionList, isLoading } = useFetchNoPaging(
    paymentService.listWorkspacePaymentPlans,
    fetchParams
  );

  // --------------- RENDER ---------------

  return (
    <>
      <div className="flex justify-between items-start gap-2">
        <div className="flex gap-4 justify-between flex-1">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {/* {selectedSolutionIds.length === 0 ? ( */}
            <>
              <FilterDropdownV2 {...statusFilterRenderProps} />
              <FilterDropdownV2 {...paymentMethodFilterRenderProps} />
            </>
            {/* ) : null} */}
          </div>
          {/* Filters end */}

          {/* Bulk actions */}
          <div className="flex gap-2 flex-wrap justify-end">
            {/* {selectedSolutionIds.length > 0 ? (
              <ButtonV2
                variant="secondary"
                color="brand"
                // onClick={enableMultipleBackup}
              >
                <CreditCardCheck className="h-5 w-5 min-w-5" />
                {t("bulkAction.renew")}
              </ButtonV2>
            ) : null} */}
          </div>
          {/* Bulk actions end */}
        </div>

        {/* Searchbox */}
        <SearchboxV2
          value={searchKeyword}
          onChange={(value) => {
            setSearchKeyword(value);
          }}
          placeholder={t("filter.searchbox.placeholder")}
        />
        {/* Searchbox end */}
      </div>

      <TableV2<IWorkspacePaymentPlanItem>
        data={solutionList ?? []}
        getRowKey={(item) => item.product.name.en}
        isLoading={isLoading}
        columns={[
          {
            title: t("header.product"),
            render: (item) =>
              locale === "vi" ? item.product.name.vi : item.product.name.en,
            gridTemplateValue: "1fr",
            overwriteWrapperProps: {
              className: "text-sm font-medium text-gray-v2-900",
            },
          },
          {
            title: t("header.solution"),
            render: (item) =>
              locale === "vi"
                ? item.product.category.name.vi
                : item.product.category.name.en,
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.subscription"),
            render: (item) =>
              locale === "vi"
                ? item.product_plan.name.vi
                : item.product_plan.name.en,
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.cycle"),
            render: (item) => {
              const findRemappedDuration = expandPlanDuration(item);
              return findRemappedDuration
                ? findRemappedDuration.getSpan()
                : item.duration;
            },
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.price"),
            // Inline typeguard for recurring_price. Sorry.
            render: (item) =>
              (((price): price is IRecurringPrice =>
                typeof price === "object" && Object.hasOwn(price, "vnd"))(
                item.product_plan.recurring_price
              ) && item.product_plan.recurring_price.vnd
                ? item.product_plan.recurring_price.vnd.toLocaleString()
                : "0") + " VND",
          },
          {
            title: t("header.expiryDate"),
            render: (item) =>
              item.end_period
                ? DateTime.fromSeconds(item.end_period).toFormat("dd/MM/yyyy")
                : "-",
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.paymentMethod"),
            render: (item) =>
              item.payment_method === PaymentMethodValue.BANKING ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.paymentMethod.banking")}
                  icon={
                    <Repeat02 className="h-3 w-3 min-w-3 text-blue-light-v2" />
                  }
                  color={"blue-light"}
                />
              ) : item.payment_method === PaymentMethodValue.CARD ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.paymentMethod.card")}
                  icon={
                    <CreditCard02 className="h-3 w-3 min-w-3 text-blue-light-v2" />
                  }
                  color={"blue-light"}
                  overwriteClassName="bg-white"
                />
              ) : (
                "-"
              ),
            gridTemplateValue: "1fr",
          },
          {
            title: t("header.status"),
            render: (item) =>
              item.status === SolutionStatusValue.ACTIVE ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.status.active")}
                  icon={
                    <CheckCircle className="h-3 w-3 min-w-3 text-success-v2" />
                  }
                  color={"success"}
                />
              ) : item.status === SolutionStatusValue.EXPIRING ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.status.expiring")}
                  icon={<Clock className="h-3 w-3 min-w-3 text-warning-v2" />}
                  color={"warning"}
                />
              ) : item.status === SolutionStatusValue.EXPIRED ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.status.expired")}
                  icon={
                    <InfoCircle className="h-3 w-3 min-w-3 text-error-v2" />
                  }
                  color={"error"}
                />
              ) : (
                "-"
              ),
            gridTemplateValue: "1fr",
          },
          {
            title: null,
            render: (item) => (
              <TableActionButtonV2
                actionList={actionList.filter((action) => {
                  if (action.key === SolutionAction.UNSUBSCRIBE) {
                    // We don't let user unsubscribe this product since it causes changes on other products.
                    if (item.product_plan.is_free_plan) {
                      return false;
                    }
                    // Expired plans shouldn't be abled to be canceled.
                    if (item.status === SolutionStatusValue.EXPIRED) {
                      return false;
                    }
                    // TODO: add more checks?
                    return true;
                  }
                  if (action.key === SolutionAction.RENEW) {
                    // Free product cannot be renewed.
                    if (item.product_plan.is_free_plan) {
                      return false;
                    }
                    // Only allow renew when payment method is bank_transfer
                    if (item.payment_method !== PaymentMethodValue.BANKING) {
                      return false;
                    }
                  }
                  if (action.key === SolutionAction.UPGRADE_PLAN) {
                    if (item.product.id !== "trust_center") {
                      return false;
                    }
                  }
                  return true;
                })}
                data={item}
              />
            ),
            gridTemplateValue: "3.25rem",
            overwriteWrapperProps: { className: "p-0 justify-center" },
          },
        ]}
        // rowSelection={{
        //   selectedRowKeys: selectedSolutionIds,
        //   onChange: setSelectedSolutionIds,
        // }}
        options={{ fitPageHeight: true }}
      />
      <BillingServiceUnsubModal
        unsubbingService={unsubbingService}
        onClose={onCloseUnsubModal}
        currentWorkspace={currentWorkspace}
      />
    </>
  );
};
